import { gql } from '@apollo/client';

// ─── Account Members Page Queries ───────────────────────────────────────────

/**
 * UserMember fragment — full member fields for account-members page.
 * Matches backend: company.model.ts → UserMember class
 */
const USER_MEMBER_FIELDS = `
  id
  name
  email
  role
  toRole
  date
  canEditHomePath
  multiFactorConfig {
    totp
    phone
  }
`;

/**
 * Get company info with full user members list + MFA data (default team).
 * Uses getCompanyInfoWithUserMfa which enriches users with multiFactorConfig
 * from the users collection (not stored in denormalized company.users array).
 *
 * Matches scio2: AccountMembersService.getCompanyInfo() → getCompanyInfoWithUserMfa
 * Backend: company.resolver.ts → getCompanyInfoWithUserMfa
 */
export const GET_COMPANY_WITH_MEMBERS = gql`
  query GetCompanyWithMembers($teamId: String) {
    getCompanyInfoWithUserMfa(teamId: $teamId) {
      _id
      name
      payment {
        planFeature
        plan
      }
      limitation {
        maxQuantity
        quantity
      }
      feature {
        allowTeams
        samlSSO
        roleSecurity
        enforceSSO
      }
      users {
        ${USER_MEMBER_FIELDS}
      }
      activatedInvitedUserCount
    }
  }
`;

/**
 * Get paginated teams with full member lists (excludes synthetic root team).
 * Uses the `teamsPaginated` query — returns only real teams the user has access to.
 * Root admins see all teams; team-only users see only their teams.
 *
 * Backend: team.resolver.ts → teamsPaginated query (offset-based pagination, no root team injection)
 */
export const GET_TEAMS_WITH_MEMBERS = gql`
  query GetTeamsWithMembers($teamId: String, $first: Int, $offset: Int, $search: String, $roles: [String!], $scope: String, $sort: TeamSort) {
    teamsPaginated(teamId: $teamId, first: $first, offset: $offset, search: $search, roles: $roles, scope: $scope, sort: $sort) {
      page {
        edges {
          node {
            _id
            name
            description
            limitation {
              maxQuantity
              quantity
            }
            users {
              ${USER_MEMBER_FIELDS}
            }
            isTeamBilling
            # #2263 EFFECTIVE membership — direct members ∪ the members of every group granted to the
            # teamspace, deduped (backend TeamService.enrichTeamsWithMemberCount). The Teamspaces row
            # used to count users[] only, which contradicted the teamspace's own roster.
            memberCount
            # §12 HOME PATH at the teamspace level — Edit Teamspace seeds its toggle from this.
            # Omitting it makes the dialog open UNCHECKED on a team that has it on, and the save
            # would then write false back: a silent revocation on an unrelated edit.
            canEditHomePath
            # scio3#2365 Folder access level — same reason: the select seeds from it.
            folderAccess
            # Teamspace icon (lucide name + #rrggbb); unset = the name's first letter.
            icon
            iconColor
          }
        }
      }
      pageData {
        total
      }
    }
  }
`;

// ─── Queries ────────────────────────────────────────────────────────────────

/**
 * Get custom roles for the account.
 * Used in role dropdowns (invite, change role, add user dialogs).
 *
 * Matches scio2: AccountMembersService.getCustomRoles()
 * Backend: role.resolver.ts → customRoles query
 */
export const GET_CUSTOM_MEMBER_ROLES = gql`
  query GetCustomRoles($first: Int) {
    customRoles(first: $first) {
      page {
        edges {
          node {
            _id
            name
            description
            teamId
            createdAt
          }
        }
      }
    }
  }
`;

/**
 * Get custom roles with full permissions data — used in ManageRolesDialog detail view.
 *
 * Backend: role.resolver.ts → customRoles query (same query, more fields)
 */
export const GET_CUSTOM_ROLES_WITH_PERMISSIONS = gql`
  query GetCustomRolesWithPermissions($first: Int) {
    customRoles(first: $first) {
      page {
        edges {
          node {
            _id
            name
            description
            createdAt
            roleType
            permissions {
              screenCanCreate
              screenCanEdit
              screenCanDelete
              screenCanTag
              assetCanCreate
              assetCanEdit
              assetCanDelete
              assetCanTag
              playlistCanCreate
              playlistCanEdit
              playlistCanDelete
              playlistCanTag
              playlistCanPropose
              playlistCanApprove
              scheduleCanCreate
              scheduleCanEdit
              scheduleCanDelete
            }
            # v5 §4b — the capability-plane keys. MUST be selected: the dialog round-trips what it
            # reads, so omitting this would silently clear every non-legacy permission on save.
            permissionKeys
          }
        }
      }
    }
  }
`;

/**
 * The permission registry (v5 §3a) — every `<domain>.<verb>` the backend enforces.
 *
 * Served by the backend rather than duplicated here on purpose: the custom-role matrix can then only
 * offer keys some guard actually consumes, so a checkbox can never be a control that does nothing.
 *
 * Backend: role.resolver.ts → permissionRegistry
 */
export const GET_PERMISSION_REGISTRY = gql`
  query GetPermissionRegistry {
    permissionRegistry {
      key
      domain
      verb
      domainGroup
      planFeature
      description
    }
  }
`;

/**
 * What each BUILT-IN role can do — the companion to `GET_PERMISSION_REGISTRY`.
 *
 * The registry says which permissions exist; this says which of them a system role holds. Served for
 * the same anti-drift reason: a system role has no `roles` document to read `permissionKeys` from
 * (its bundle is code), so the only alternative is a local copy — and the local copy we had was the
 * 17 content booleans in `systemRoles.ts`, which left every Operate/Administer row unticked for
 * Owner in the role detail view.
 *
 * Backend: role.resolver.ts → systemRolePermissions
 */
export const GET_SYSTEM_ROLE_PERMISSIONS = gql`
  query GetSystemRolePermissions {
    systemRolePermissions {
      role
      fullAccess
      permissionKeys
    }
  }
`;

/**
 * Create a new custom role with permissions.
 *
 * Backend: role.resolver.ts → createRole
 * Migrated from: Roles.insert({ ...role })
 */
export const CREATE_ROLE = gql`
  mutation CreateRole($input: CreateRoleInput!) {
    createRole(input: $input) {
      _id
      name
    }
  }
`;

/**
 * Update an existing custom role.
 *
 * Backend: role.resolver.ts → updateRole
 * Migrated from: Roles.update(role._id, { $set: { ...role } })
 */
export const UPDATE_ROLE = gql`
  mutation UpdateRole($input: UpdateRoleInput!) {
    updateRole(input: $input) {
      _id
      name
    }
  }
`;

/**
 * Delete a custom role.
 *
 * Backend: role.resolver.ts → deleteRole
 * Migrated from: Roles.remove(role._id)
 */
export const DELETE_ROLE = gql`
  mutation DeleteRole($input: DeleteRoleInput!) {
    deleteRole(input: $input)
  }
`;

/**
 * Get users available to add to a team (not already members).
 * Returns both available users (no member flag) and existing members (member='0').
 *
 * Backend: member.resolver.ts → getAvailableUserForTeam
 */
export const GET_AVAILABLE_USERS_FOR_TEAM = gql`
  query GetAvailableUserForTeam($input: GetAvailableUserForTeamInput!) {
    getAvailableUserForTeam(input: $input) {
      _id
      name
      email
      role
      member
    }
  }
`;

/**
 * Get max available screens for team allocation.
 * Used in TeamDialog to show how many screens can be assigned.
 *
 * Backend: member.resolver.ts → getMaxScreenAvailable
 */
export const GET_MAX_SCREEN_AVAILABLE = gql`
  query GetMaxScreenAvailable($input: GetMaxScreenAvailableInput!) {
    getMaxScreenAvailable(input: $input)
  }
`;

// ─── Mutations ──────────────────────────────────────────────────────────────

/**
 * Remove MFA (2FA) for a user. Admin action for users who lost their authenticator.
 *
 * Backend: member.resolver.ts → removeMFAForUser
 * Migrated from: Meteor.call('removeMFAForUser', _id, teamId)
 */
export const REMOVE_MFA_FOR_USER = gql`
  mutation RemoveMFAForUser($input: RemoveMFAForUserInput!) {
    removeMFAForUser(input: $input)
  }
`;

/**
 * Invite a user to join a team or root company via email.
 * Validates reCAPTCHA. For existing users, adds team to teamIds.
 * For new users, sends invitation email.
 *
 * Backend: member.resolver.ts → inviteUser
 * Migrated from: Meteor.call('inviteUser', name, email, response, role, teamId)
 */
export const INVITE_USER = gql`
  mutation InviteUser($input: InviteUserInput!) {
    inviteUser(input: $input)
  }
`;

/**
 * Add an existing user to a team (user must already exist in system).
 *
 * Backend: member.resolver.ts → addMember
 * Migrated from: Meteor.call('addMember', _id, role, teamId)
 */
export const ADD_MEMBER = gql`
  mutation AddMember($input: AddMemberInput!) {
    addMember(input: $input)
  }
`;

/**
 * Remove member(s) from a team. Does NOT delete the user account.
 *
 * Backend: member.resolver.ts → removeMember
 * Migrated from: Meteor.call('removeMember', emails, teamId)
 */
export const REMOVE_MEMBER = gql`
  mutation RemoveMember($input: RemoveMemberInput!) {
    removeMember(input: $input)
  }
`;

/**
 * Permanently delete user(s) from system (MongoDB + Firebase Auth).
 *
 * Backend: member.resolver.ts → deleteUser
 * Migrated from: Meteor.call('deleteUser', _ids)
 */
export const DELETE_USER = gql`
  mutation DeleteUser($input: DeleteUserInput!) {
    deleteUser(input: $input)
  }
`;

/**
 * Change a user's role in a team or root company.
 * Handles ownership transfer if changing to OWNER role.
 *
 * Backend: member.resolver.ts → changeRole
 * Migrated from: Meteor.call('changeRole', _id, role, teamId)
 */
export const CHANGE_ROLE = gql`
  mutation ChangeRole($input: ChangeRoleInput!) {
    changeRole(input: $input)
  }
`;

/**
 * Create a new user account with password. Returns user ID.
 * Validates reCAPTCHA. Creates in MongoDB + Firebase Auth.
 *
 * Backend: member.resolver.ts → createNewUser
 * Migrated from: Meteor.call('createNewUser', user, notifyEmail)
 */
export const CREATE_NEW_USER = gql`
  mutation CreateNewUser($input: CreateNewUserInput!) {
    createNewUser(input: $input)
  }
`;

/**
 * Send login credentials email to newly created user.
 * Email includes username/email + password.
 *
 * Backend: member.resolver.ts → sendNewUserLoginEmail
 * Migrated from: Meteor.call('sendNewUserLoginEmail', user, notifyEmail, scio20)
 */
export const SEND_NEW_USER_LOGIN_EMAIL = gql`
  mutation SendNewUserLoginEmail($input: SendNewUserLoginEmailInput!) {
    sendNewUserLoginEmail(input: $input)
  }
`;

/**
 * Admin sets password for user(s). Updates Firebase and MongoDB.
 * Optionally requires password change on next login and logs out user.
 *
 * Backend: member.resolver.ts → setPasswords
 * Migrated from: Meteor.call('setPasswords', userIds, newPassword, passwordChangeRequire, logout)
 */
export const SET_PASSWORDS = gql`
  mutation SetPasswords($input: SetPasswordsInput!) {
    setPasswords(input: $input)
  }
`;

/**
 * Send password reset notification email with new password set by admin.
 *
 * Backend: member.resolver.ts → sendResetPasswordEmail
 * Migrated from: Meteor.call('sendResetPasswordEmail', user, notifyEmail, scio20)
 */
export const SEND_RESET_PASSWORD_EMAIL = gql`
  mutation SendResetPasswordEmail($input: SendResetPasswordEmailInput!) {
    sendResetPasswordEmail(input: $input)
  }
`;

/**
 * Create or update a team. If _id is omitted, creates new team.
 * Validates team name uniqueness and screen quota.
 *
 * Backend: member.resolver.ts → updateTeam
 * Migrated from: Meteor.call('updateTeam', team, isReturn)
 */
export const UPDATE_TEAM = gql`
  mutation UpdateTeam($input: UpdateTeamInput!) {
    updateTeam(input: $input) {
      _id
      name
      description
      icon
      iconColor
      limitation {
        maxQuantity
        quantity
      }
    }
  }
`;

/**
 * Remove a team. Checks for active billing and SAML SSO references.
 *
 * Backend: member.resolver.ts → removeTeam
 * Migrated from: Meteor.call('removeTeam', teamId)
 */
export const REMOVE_TEAM = gql`
  mutation RemoveTeam($input: RemoveTeamInput!) {
    removeTeam(input: $input)
  }
`;

/**
 * What deleting a teamspace leaves behind (#2376): groups granted on it, groups it is the LAST
 * teamspace of, and members who lose their only teamspace. Read-only; shown as a warning in the
 * Delete Teamspace dialog — the delete itself is not blocked.
 */
export const TEAM_DELETE_IMPACT_QUERY = gql`
  query TeamDeleteImpact($teamId: String!) {
    teamDeleteImpact(teamId: $teamId) {
      groupsLosingTeamspaceCount
      groupsLeftEmptyCount
      membersLosingAccessCount
    }
  }
`;

/**
 * Check if a user is new to OptiSigns but already exists in another app (OptiDev/OptiSound/OptiAds).
 * These apps share Firebase Authentication — the user already has a password.
 *
 * Backend: member.resolver.ts → isNewUserFromCrossApp
 */
export const IS_NEW_USER_FROM_CROSS_APP = gql`
  query IsNewUserFromCrossApp($email: String!) {
    isNewUserFromCrossApp(email: $email)
  }
`;

/**
 * Set (or clear, with `color: null`) a role's badge colour for the whole account. `color` is a
 * palette key (`slate` … `purple`, see apps/web `role-badge-colors.ts`). Returns the account's full
 * `{ [roleId]: color }` map, which the caller writes into `me.featureData.roleBadgeColors`.
 */
export const SET_ROLE_BADGE_COLOR = gql`
  mutation SetRoleBadgeColor($roleId: String!, $color: String) {
    setRoleBadgeColor(roleId: $roleId, color: $color)
  }
`;
